import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  ReturnsFlatTeamSettingGroupById,
  type ReturnsFlatTeamSettingGroupId,
} from "./groups";
import { offOn, onOff } from "./shared";

export interface ReturnsSettingInput extends SettingRegistryMetadataInput {}

export function setting(metadata: ReturnsSettingInput): ReturnsSettingInput {
  return metadata;
}

export function returnsGroup<
  const Group extends ReturnsFlatTeamSettingGroupId,
  const T extends Record<string, ReturnsSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const ReturnsFlatSettingCatalogRegistry = {
  ...returnsGroup("returns.ui_return_details", {
    "returns/ui/defaultCollapsed": setting({
      label: "Collapse return details by default",
      description:
        "Starts the return details section collapsed when a merchant opens a return.",
      synonyms: [
        "collapse return details",
        "hide return details",
        "return details collapsed",
      ],
      valueSemantics: offOn,
    }),
    "returns/ui/aiReviewDefaultCollapsed": setting({
      label: "Collapse AI review by default",
      description:
        "Starts the AI review section collapsed when a merchant opens a return.",
      synonyms: ["collapse AI review", "hide AI review", "AI review collapsed"],
      valueSemantics: offOn,
    }),
    "returns/ui/lineItemDetailOptions": setting({
      label: "Line item details shown on returns",
      description:
        "Which product details — such as SKU, barcode, or price — appear on each returned line item.",
      synonyms: [
        "line item details",
        "show SKU on return",
        "return line item fields",
        "what shows on returned items",
      ],
    }),
    "returns/ui/productMetafieldDisplays": setting({
      label: "Product metafields shown on returns",
      description:
        "Product metafields surfaced on returned line items, with an optional display name for each.",
      synonyms: [
        "product metafields",
        "show metafields on returns",
        "custom product fields on returns",
      ],
    }),
    "returns/ui/variantMetafieldDisplays": setting({
      label: "Variant metafields shown on returns",
      description:
        "Variant metafields surfaced on returned line items, with an optional display name for each.",
      synonyms: [
        "variant metafields",
        "show variant metafields",
        "custom variant fields on returns",
      ],
    }),
  }),
  ...returnsGroup("returns.ui_item_disposition", {
    "returns/itemDisposition/enabled": setting({
      label: "Enable item disposition",
      description:
        "Lets warehouse staff record a grade and outcome for each returned item while processing.",
      synonyms: [
        "item disposition",
        "enable disposition",
        "grade returned items",
        "disposition returns",
      ],
      valueSemantics: offOn,
    }),
    "returns/itemDisposition/gradeOptions": setting({
      label: "Disposition grade options",
      description:
        "The condition grades staff can assign to a returned item, such as New, Used, or Damaged.",
      synonyms: [
        "grade options",
        "return grades",
        "item condition options",
        "damaged grade",
      ],
    }),
    "returns/itemDisposition/outcomeOptions": setting({
      label: "Disposition outcome options",
      description:
        "What staff can do with a returned item after grading it, such as Restock, Refurbish, or Discard.",
      synonyms: [
        "outcome options",
        "disposition outcomes",
        "restock or discard",
        "what to do with returned item",
      ],
    }),
  }),
  ...returnsGroup("returns.ui_barcodes", {
    "returns/barcode/enabled": setting({
      label: "Show barcodes",
      description:
        "Prints a scannable barcode on return labels so staff can look items up by scanning.",
      synonyms: ["show barcodes", "enable barcodes", "print barcode on return"],
      valueSemantics: offOn,
    }),
    "returns/barcode/width": setting({
      label: "Barcode width",
      description: "Width of the printed barcode label, in inches.",
      synonyms: ["barcode width", "barcode label size", "label width"],
      changeGuidance: {
        summary: "Must match the physical label stock loaded in the printer.",
      },
    }),
    "returns/barcode/height": setting({
      label: "Barcode height",
      description: "Height of the printed barcode label, in inches.",
      synonyms: ["barcode height", "barcode label size", "label height"],
      changeGuidance: {
        summary: "Must match the physical label stock loaded in the printer.",
      },
    }),
    "returns/barcode/topDisplayOption": setting({
      label: "Text above barcode",
      description: "What is printed on the line above the barcode.",
      synonyms: ["text above barcode", "barcode header", "barcode top text"],
    }),
    "returns/barcode/bottomDisplayOption": setting({
      label: "Text below barcode",
      description: "What is printed on the line below the barcode.",
      synonyms: ["text below barcode", "barcode footer", "barcode bottom text"],
    }),
  }),
  ...returnsGroup("returns.ui_gift_receipts", {
    "returns/giftReceipts/enabled": setting({
      label: "Enable gift receipts",
      description:
        "Lets gift recipients start a return without knowing the original order details.",
      synonyms: [
        "gift receipts",
        "gift returns",
        "return a gift",
        "recipient return",
      ],
      valueSemantics: onOff,
    }),
    "returns/giftReceipts/missingBuyerInfoDescription": setting({
      label: "Missing buyer info instructions",
      description:
        "The instructions a gift recipient sees when Redo cannot identify the original purchaser.",
      synonyms: [
        "missing buyer info",
        "gift return instructions",
        "buyer information message",
      ],
    }),
  }),
  ...returnsGroup("returns.ui_external_ra_number", {
    "returns/ui/exposeExternalId": setting({
      label: "Expose external ID",
      description:
        "Shows the return authorization number from a connected external system on the return.",
      synonyms: [
        "external RA number",
        "expose external id",
        "return authorization number",
        "show RA number",
      ],
      valueSemantics: offOn,
    }),
  }),
  ...returnsGroup("returns.ui_return_editing", {
    "returns/returnEditing/allowCustomerCancelReturn": setting({
      label: "Allow customers to cancel returns",
      description:
        "Lets a customer cancel a return themselves after submitting it.",
      synonyms: [
        "customer cancel return",
        "cancel return",
        "let customers cancel",
        "undo return",
      ],
      valueSemantics: offOn,
    }),
    "returns/returnEditing/allowCustomerEditExchangeAddress": setting({
      label: "Allow customers to edit exchange address",
      description:
        "Lets a customer change the shipping address on an exchange after submitting the return.",
      synonyms: [
        "edit exchange address",
        "change shipping address",
        "customer edit address",
      ],
      valueSemantics: offOn,
    }),
  }),
  ...returnsGroup("returns.processing", {
    "returns/processing/shopAgainAction": setting({
      label: "Store credit type",
      description:
        "Where store credit is issued when a return is processed — a Shopify gift card, a discount code, a customer account balance, or a connected loyalty provider.",
      synonyms: [
        "store credit type",
        "gift card",
        "discount code",
        "how store credit is issued",
        "credit provider",
        "rise credit",
      ],
      risk: {
        level: "high",
        reason: "Changes how every future store credit payout is delivered.",
      },
      changeGuidance: {
        summary:
          "Confirm the destination provider is connected before switching — an unconnected provider silently fails to issue credit.",
      },
    }),
    "returns/processing/riseTriggerUrl": setting({
      label: "Rise trigger URL",
      description:
        "The Rise custom trigger URL Redo calls to issue store credit.",
      synonyms: ["rise trigger", "rise URL", "rise credit webhook"],
      dependencies: [
        {
          key: "returns/processing/shopAgainAction",
          relationship: "controlledBy",
          description: "Only used when store credit is issued through Rise.",
        },
      ],
    }),
    "returns/processing/giftCardOrderProductId": setting({
      label: "Gift card product",
      description:
        "The Shopify gift card product Redo orders when issuing store credit as a gift card order.",
      synonyms: [
        "gift card product",
        "gift card order product",
        "which gift card",
      ],
      dependencies: [
        {
          key: "returns/processing/shopAgainAction",
          relationship: "controlledBy",
          description:
            "Only used when store credit is issued as a gift card order.",
        },
      ],
    }),
    "returns/processing/hideGiftCardBranding": setting({
      label: "Hide Redo gift card branding",
      description:
        "Replaces the REDO prefix on issued gift card codes with random characters.",
      synonyms: [
        "hide redo branding",
        "gift card branding",
        "REDO prefix",
        "white label gift card",
      ],
      valueSemantics: offOn,
    }),
    "returns/processing/allowRestockBeforeProcess": setting({
      label: "Decouple restock from return processing",
      description:
        "Lets staff restock returned items before the return is processed, instead of only as part of processing.",
      synonyms: [
        "decouple restock",
        "restock before processing",
        "restock separately",
      ],
      valueSemantics: offOn,
    }),
    "returns/processing/checkForTryNowTags": setting({
      label: "Check for TryNow product tags",
      description:
        "Looks for TryNow product tags so Redo skips creating refunds on TryNow items.",
      synonyms: ["TryNow", "try now tags", "trynow product tags"],
      valueSemantics: offOn,
    }),
  }),
};

export type ReturnsFlatTeamSettingKey =
  keyof typeof ReturnsFlatSettingCatalogRegistry;

const ReturnsFlatTeamSettingKeys = Object.keys(
  ReturnsFlatSettingCatalogRegistry,
) as ReturnsFlatTeamSettingKey[];

const ReturnsFlatTeamSettingKeySet = new Set<string>(
  ReturnsFlatTeamSettingKeys,
);

export function isReturnsFlatTeamSettingKey(
  key: string,
): key is ReturnsFlatTeamSettingKey {
  return ReturnsFlatTeamSettingKeySet.has(key);
}

export const ReturnsFlatTeamSettingCatalogDefinitions =
  ReturnsFlatTeamSettingKeys.map((key) => {
    const definition = ReturnsFlatSettingCatalogRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: ReturnsFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    ReturnsFlatTeamSettingKey,
    ReturnsFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<ReturnsFlatTeamSettingKey>
  >[];

export const ReturnsFlatTeamSettingMetadataByKey = Object.fromEntries(
  ReturnsFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<ReturnsFlatTeamSettingKey, SettingMetadata>;
